import axios from "axios";
import { useState } from "react";
import { useSetRecoilState } from "recoil";

import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  AlertTitle,
  Button,
  Container,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import ConstList from "../utils/ConstList";
import { maintenanceState } from "../stores/MaintenanceAtom";

import Loading from "./Loading";

type MaintenanceEditData = {
  access_token: string;
  algorithm: string;
  flag: string;
  start: string;
  end: string;
  day: string;
  maintenanceFlag: boolean | number;
  display: boolean | number;
};

type Props = {
  setEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: MaintenanceEditData;
};

type ConfirmData = {
  itemName: string;
  data: string;
};

function MaintenanceEdit({ setEditOpen, data }: Props) {
  const [completed, setCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  const setMaintenance = useSetRecoilState(maintenanceState);

  const URL = ConstList.getApiUrl() + "UpdateMaintenance.php";

  /*
   * propsのdataを直接書き換えないように、
   * API送信用のデータを新しく作成する。
   */
  const maintenanceFlagValue = data.maintenanceFlag ? 1 : 0;
  const displayValue = data.display ? 1 : 0;

  const requestData = {
    ...data,
    maintenanceFlag: maintenanceFlagValue,
    display: displayValue,
  };

  const flagName = maintenanceFlagValue === 1 ? "オン" : "オフ";
  const displayName = displayValue === 1 ? "オン" : "オフ";

  const createData = (itemName: string, value: unknown): ConfirmData => {
    return {
      itemName,
      data:
        value === null || value === undefined
          ? ""
          : typeof value === "string"
            ? value
            : String(value),
    };
  };

  const confirmData: ConfirmData[] = [
    createData("メンテナンス表示", displayName),
    createData("メンテナンス予定日", data.day),
    createData("開始時刻", data.start),
    createData("終了時刻", data.end),
    createData("メンテナンスモード", flagName),
  ];

  const getResponseMessage = (
    message: unknown,
    fallbackMessage: string,
  ): string => {
    if (typeof message === "string") {
      return message;
    }

    if (message === null || message === undefined) {
      return fallbackMessage;
    }

    try {
      return JSON.stringify(message);
    } catch {
      return fallbackMessage;
    }
  };

  const registerEdit = async () => {
    setLoading(true);
    setErrorMessage("");
    setActionMessage("");
    setOpenErrorAlert(false);

    try {
      const response = await axios.post(
        URL,
        requestData,
        ConstList.AXIOS_CONFIG,
      );

      console.log("メンテナンス設定変更結果:", response.data);

      const responseMessage = getResponseMessage(
        response.data?.message,
        "処理に失敗しました。",
      );

      setActionMessage(responseMessage);

      if (response.data?.data === false) {
        setLoading(false);
        setOpenErrorAlert(true);

        const redirectUrl = response.data?.reDirect;

        if (typeof redirectUrl === "string" && redirectUrl.trim() !== "") {
          await ConstList.SLEEP(5000);
          window.location.href = redirectUrl;
        }

        return;
      }

      /*
       * response.data.data に最新のメンテナンス設定が
       * 入っている前提。
       */
      if (response.data?.data) {
        setMaintenance(response.data.data);
      }

      setCompleted(true);
      setLoading(false);
    } catch (caughtError: unknown) {
      console.error("メンテナンス設定変更エラー:", caughtError);

      let message = "設定の変更中にエラーが発生しました。";

      if (axios.isAxiosError(caughtError)) {
        const apiMessage = caughtError.response?.data?.message;

        if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
          message = apiMessage;
        } else if (
          typeof caughtError.response?.data === "string" &&
          caughtError.response.data.trim() !== ""
        ) {
          message = caughtError.response.data;
        } else if (caughtError.message) {
          message = caughtError.message;
        }
      } else if (caughtError instanceof Error) {
        message = caughtError.message;
      } else if (typeof caughtError === "string") {
        message = caughtError;
      }

      /*
       * Errorオブジェクトそのものではなく、
       * 必ず文字列だけをstateに保存する。
       */
      setErrorMessage(message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <DialogTitle id="scroll-dialog-title">設定の変更</DialogTitle>

        <DialogContent dividers>
          <Loading />
        </DialogContent>
      </>
    );
  }

  if (errorMessage) {
    return (
      <>
        <DialogTitle id="scroll-dialog-title">
          設定の変更 ＜エラー＞
        </DialogTitle>

        <DialogContent dividers>
          <Container maxWidth="sm" sx={{ py: 5 }}>
            <Stack spacing={3}>
              <Alert severity="error">
                <AlertTitle>設定の変更に失敗しました</AlertTitle>

                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {errorMessage}
                </Typography>
              </Alert>
            </Stack>
          </Container>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>閉じる</Button>
        </DialogActions>
      </>
    );
  }

  if (completed) {
    return (
      <>
        <DialogTitle id="scroll-dialog-title">設定の変更 ＜完了＞</DialogTitle>

        <DialogContent dividers>
          <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
            設定の変更内容が正常に保存されました。
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>閉じる</Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogTitle id="scroll-dialog-title">
        設定の変更 ＜確認画面＞
      </DialogTitle>

      <DialogContent dividers>
        <DialogContentText
          id="scroll-dialog-description"
          tabIndex={-1}
          sx={{ mb: 2 }}
        >
          内容をご確認の上、保存ボタンを押してください。
        </DialogContentText>

        <Table aria-label="メンテナンス設定確認">
          <TableHead>
            <TableRow>
              <TableCell>設定項目</TableCell>
              <TableCell>設定内容</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {confirmData.map((row) => (
              <TableRow key={row.itemName}>
                <TableCell component="th" scope="row">
                  {row.itemName}
                </TableCell>

                <TableCell>{row.data}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setEditOpen(false)}>閉じる</Button>

        <Button variant="contained" onClick={registerEdit} disabled={loading}>
          保存
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={openErrorAlert}
        onClose={() => setOpenErrorAlert(false)}
        autoHideDuration={10000}
      >
        <Alert
          variant="filled"
          severity="error"
          onClose={() => setOpenErrorAlert(false)}
          sx={{ whiteSpace: "pre-line", width: "100%" }}
        >
          <AlertTitle>エラー</AlertTitle>
          {actionMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default MaintenanceEdit;
